import { ReactNode } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../modules/localization/translations"
import { MeetingDetailsOrder } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { DeliveryType } from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"

const useStyles = makeStyles()((theme) => ({
    detailsCard: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".75rem",
        overflow: "hidden",
    },
    detailRow: {
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
        "&:last-child": { borderBottom: "none" },
    },
    detailRowTime: {
        background: theme.palette.tailwind.emerald[50],
    },
    detailLabel: {
        color: theme.palette.tailwind.slate[500],
    },
    detailValue: {
        color: theme.palette.tailwind.slate[800],
        textAlign: "right",
    },
    detailValueTime: {
        color: theme.palette.tailwind.emerald[700],
    },
    noteCallout: {
        marginTop: "1rem",
        padding: ".75rem 1rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.amber[200]}`,
        background: theme.palette.tailwind.amber[50],
    },
    noteTitle: {
        color: theme.palette.tailwind.amber[700],
    },
    noteText: {
        color: theme.palette.tailwind.slate[700],
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
}))

type Props = Readonly<{
    details: MeetingDetailsOrder
}>

export function OrderDetailsCard({ details }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const sittings = details.deliveryDetails.sittings ?? 0
    const contactName = details.customer?.displayName ?? ""
    const deliveryType = details.deliveryDetails.type
    const deliveryTypeLabel = deliveryType
        ? l10n.getStringForEnum("delivery-type-filter", deliveryType, deliveryType)
        : ""
    const isCanteenOrPickup = deliveryType === DeliveryType.EATING_IN_CANTEEN || deliveryType === DeliveryType.PICK_UP
    const locationName = details.deliveryDetails.location?.name ?? ""
    const deliveryDateTime = dateTimeFactory.fromApi(details.deliveryDetails.time ?? "")
    const deliveryTimeLabel = deliveryDateTime.isValid
        ? `${dateTimeFactory.formatDayAndDate(deliveryDateTime)} · ${dateTimeFactory.formatTime(deliveryDateTime)}`
        : "-"

    const detailRows: ReadonlyArray<{ key: string; labelId: TranslationKey; labelFallback: string; value: ReactNode }> =
        [
            {
                key: "contact",
                labelId: "meeting-service-order-drawer-contact",
                labelFallback: "Contact",
                value: contactName,
            },
            {
                key: "covers",
                labelId: "meeting-service-order-drawer-covers",
                labelFallback: "Covers",
                value: (
                    <LocalizedStrict id="meeting-service-order-drawer-covers-value" vars={{ count: sittings }}>
                        <span>{`${sittings} people`}</span>
                    </LocalizedStrict>
                ),
            },
            {
                key: "type",
                labelId: "meeting-service-order-drawer-delivery-type",
                labelFallback: "Delivery type",
                value: deliveryTypeLabel,
            },
            ...(isCanteenOrPickup || !locationName
                ? []
                : [
                      {
                          key: "location",
                          labelId: "meeting-service-order-drawer-location" as TranslationKey,
                          labelFallback: "Location",
                          value: locationName,
                      },
                  ]),
        ]

    return (
        <>
            <Box className={classes.detailsCard}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing="1rem"
                    className={cx(classes.detailRow, classes.detailRowTime)}
                >
                    <LocalizedStrict id="meeting-service-order-drawer-delivery-time">
                        <Typography component="span" variant="body2" fontWeight={600} className={classes.detailLabel}>
                            Delivery time
                        </Typography>
                    </LocalizedStrict>
                    <Typography
                        component="span"
                        variant="body2"
                        fontWeight={700}
                        className={cx(classes.detailValue, classes.detailValueTime)}
                    >
                        {deliveryTimeLabel}
                    </Typography>
                </Stack>
                {detailRows.map((row) => (
                    <Stack
                        key={row.key}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing="1rem"
                        className={classes.detailRow}
                    >
                        <LocalizedStrict id={row.labelId}>
                            <Typography
                                component="span"
                                variant="body2"
                                fontWeight={600}
                                className={classes.detailLabel}
                            >
                                {row.labelFallback}
                            </Typography>
                        </LocalizedStrict>
                        <Typography component="span" variant="body2" fontWeight={700} className={classes.detailValue}>
                            {row.value}
                        </Typography>
                    </Stack>
                ))}
            </Box>

            {!!details.note && (
                <Box className={classes.noteCallout}>
                    <Stack direction="row" alignItems="center" spacing=".5rem" marginBottom=".25rem">
                        <FontAwesomeIcon icon={faComment} size="sm" className={classes.noteTitle} />
                        <LocalizedStrict id="meeting-service-order-drawer-order-note">
                            <Typography
                                component="span"
                                variant="caption"
                                fontWeight={700}
                                letterSpacing=".05em"
                                className={classes.noteTitle}
                            >
                                ORDER NOTE
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                    <Typography variant="body2" className={classes.noteText}>
                        {details.note}
                    </Typography>
                </Box>
            )}
        </>
    )
}
